import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
    Dumbbell,
    Users,
    CreditCard,
    CalendarDays,
    Shield,
    Smartphone,
    Check,
    ArrowRight,
    Search,
    ChevronDown,
    BarChart3,
    Bell,
} from 'lucide-react';

const features = [
    { icon: Users, title: 'Gestión de Alumnos', desc: 'Administrá fácilmente a tus alumnos, buscá por DNI y agregá notas personalizadas para llevar un mejor seguimiento de cada uno.' },
    { icon: CreditCard, title: 'Tarjetas de Membresía', desc: 'Creá tarjetas mensuales con control de clases, vencimiento automático y estado en tiempo real.' },
    { icon: CalendarDays, title: 'Clases Flexibles', desc: 'Configurá clases fijas o variables. El sistema bloquea cambios a menos de 2 horas del inicio.' },
    { icon: Bell, title: 'Notificaciones inteligentes', desc: 'Recibí avisos importantes sobre tus clases, como cancelaciones, recordatorios para tomar agua y otras notificaciones útiles para acompañarte en tu día a día.' },
    { icon: Smartphone, title: 'Consulta por DNI', desc: 'Tus alumnos pueden consultar su tarjeta ingresando su DNI. Sin login, sin complicaciones.' },
    { icon: Dumbbell, title: 'Multi-Gimnasio', desc: 'Plataforma preparada para escalar. Cada gimnasio opera de forma independiente con su propia marca.' },
];

const plans = [
    {
        name: 'Lite',
        price: 'Consultar',
        features: ['1 gimnasio', 'Hasta 20 alumnos por gym', 'Tarjetas de membresía', 'Gestión de clases', 'Consulta por DNI'],
        popular: false,
    },
    {
        name: 'Pro',
        price: 'Consultar',
        features: ['Hasta 3 gimnasios', 'Hasta 50 alumnos por gym', 'Todo lo del plan Lite', 'Pagos y facturación', 'Soporte prioritario'],
        popular: true,
    },
    {
        name: 'Enterprise',
        price: 'Consultar',
        features: ['Gimnasios ilimitados', 'Alumnos ilimitados', 'Todo lo del plan Pro', 'Integraciones API', 'Soporte dedicado 24/7', 'Onboarding personalizado'],
        popular: false,
    },
];

export default function Landing() {
    return (
        <div className="min-h-screen bg-black text-white">
            {/* Navbar overlay */}
            <nav className="fixed top-0 left-0 right-0 z-50 bg-black/20">
                <div className="max-w-7xl mx-auto px-6 lg:px-10 py-5 flex items-center justify-between">
                    <Link to="/" className="flex items-center gap-3">
                        {/* Geometric hexagon logo */}
                        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="text-white">
                            <path d="M12 2l8.66 5v10l-8.66 5-8.66-5V7z" />
                            <path d="M12 12m-3 0a3 3 0 1 0 6 0a3 3 0 1 0 -6 0" />
                        </svg>
                        <span className="text-sm font-bold uppercase tracking-[0.2em]">GYMS.ar</span>
                    </Link>
                    <div className="flex items-center gap-2 sm:gap-5">
                        <Link to="/consulta-dni" className="hidden sm:flex items-center gap-2 text-white/90 hover:text-white transition-colors uppercase text-xs tracking-[0.15em] font-medium">
                            <Search className="h-4 w-4" />
                            Consultar DNI
                        </Link>
                        <Link to="/login" className="hidden sm:flex items-center text-white/90 hover:text-white transition-colors uppercase text-xs tracking-[0.15em] font-medium">
                            Ingresar
                        </Link>
                        <div className="flex items-center gap-2 sm:hidden">
                            <a
                                href="/auth/google/redirect"
                                aria-label="Ingresar con Google"
                                className="h-8 w-8 rounded-full bg-white flex items-center justify-center hover:opacity-90 transition-opacity"
                            >
                                <svg className="h-5 w-5" viewBox="0 0 24 24">
                                    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4" />
                                    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
                                    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
                                    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
                                </svg>
                            </a>
                            <Link to="/consulta-dni">
                                <Button variant="outline" size="sm" className="uppercase tracking-[0.15em] text-xs border-white/60 hover:border-white hover:bg-white hover:text-black">
                                    Clases
                                </Button>
                            </Link>
                        </div>
                        <Link to="/register" className="hidden sm:block">
                            <Button variant="outline" size="sm" className="uppercase tracking-[0.15em] text-xs border-white/60 hover:border-white hover:bg-white hover:text-black">
                                Registrarse
                            </Button>
                        </Link>
                    </div>
                </div>
            </nav>

            {/* Hero — full-bleed photo band */}
            <section className="relative min-h-screen flex flex-col justify-end overflow-hidden">
                <div className="absolute inset-0 bg-[url('/bg-main.webp')] bg-cover bg-center bg-no-repeat" />
                <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/60 to-black/40" />

                <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-10 pb-32 [@media(max-height:700px)]:pb-16 w-full">
                    <div className="max-w-3xl">
                        {/* Eyebrow */}
                        <div className="flex items-center gap-4 mb-8">
                            <div className="w-px h-5 bg-white/60" />
                            <span className="micro-cap text-white/70 tracking-[0.2em]">Plataforma para Gimnasios</span>
                        </div>

                        {/* Headline */}
                        <h1 className="text-white mb-8 leading-[0.95] tracking-[0.04em]">
                            Gestioná tu gimnasio de forma simple y profesional
                        </h1>

                        {/* Subtitle */}
                        <p className="text-base text-white/70 mb-10 max-w-xl leading-relaxed">
                            Alumnos, membresías, clases y pagos en una sola plataforma.
                            Diseñada para gimnasios que quieren crecer sin complicaciones.
                        </p>

                        {/* CTAs */}
                        <div className="flex flex-col sm:flex-row gap-4">
                            <Link to="/login" className="w-full sm:hidden">
                                <Button size="lg" className="w-full sm:w-auto bg-white text-black hover:bg-white/90 uppercase tracking-[0.15em] text-xs font-bold px-8">
                                    Empezar ahora <ArrowRight className="h-4 w-4 ml-2" />
                                </Button>
                            </Link>
                            <Link to="/register" className="hidden sm:block">
                                <Button size="lg" className="w-full sm:w-auto bg-white text-black hover:bg-white/90 uppercase tracking-[0.15em] text-xs font-bold px-8">
                                    Empezar ahora <ArrowRight className="h-4 w-4 ml-2" />
                                </Button>
                            </Link>
                            <Link to="/consulta-dni" className="w-full sm:w-auto">
                                <Button variant="outline" size="lg" className="w-full sm:w-auto border-white/60 text-white hover:bg-white hover:text-black uppercase tracking-[0.15em] text-xs font-bold px-8">
                                    <CreditCard className="h-4 w-4 mr-2" />Consultar mi tarjeta
                                </Button>
                            </Link>
                        </div>
                    </div>

                    {/* Bottom feature bar */}
                    <div className="hidden lg:grid lg:grid-cols-3 gap-8 mt-20 pt-8 border-t border-white/10 [@media(max-height:700px)]:hidden">
                        <div className="flex items-start gap-4">
                            <div className="h-10 w-10 rounded-full border border-white/20 flex items-center justify-center shrink-0">
                                <Users className="h-4 w-4 text-white" />
                            </div>
                            <div>
                                <p className="text-sm font-semibold uppercase tracking-wide text-white">Todo en un solo lugar</p>
                                <p className="text-xs text-white/50 mt-1">Alumnos, clases, planes y pagos.</p>
                            </div>
                        </div>
                        <div className="flex items-start gap-4">
                            <div className="h-10 w-10 rounded-full border border-white/20 flex items-center justify-center shrink-0">
                                <BarChart3 className="h-4 w-4 text-white" />
                            </div>
                            <div>
                                <p className="text-sm font-semibold uppercase tracking-wide text-white">Controlá tu gimnasio</p>
                                <p className="text-xs text-white/50 mt-1">Métricas e informes en tiempo real.</p>
                            </div>
                        </div>
                        <div className="flex items-start gap-4">
                            <div className="h-10 w-10 rounded-full border border-white/20 flex items-center justify-center shrink-0">
                                <Shield className="h-4 w-4 text-white" />
                            </div>
                            <div>
                                <p className="text-sm font-semibold uppercase tracking-wide text-white">Seguro y confiable</p>
                                <p className="text-xs text-white/50 mt-1">Tus datos siempre protegidos.</p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Scroll chevron */}
                <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-10">
                    <ChevronDown className="h-5 w-5 text-white/50 animate-bounce" />
                </div>
            </section>

            {/* Features */}
            <section className="py-24 px-6 lg:px-10 bg-[#0a0a0a]">
                <div className="max-w-7xl mx-auto">
                    <div className="mb-16">
                        <div className="flex items-center gap-4 mb-4">
                            <div className="w-px h-5 bg-white/60" />
                            <span className="micro-cap text-white/60 tracking-[0.2em]">Funcionalidades</span>
                        </div>
                        <h2 className="text-white mb-4">Todo lo que necesitás</h2>
                        <p className="text-white/60 max-w-xl text-base">Herramientas pensadas para simplificar la gestión diaria de tu gimnasio.</p>
                    </div>
                    <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
                        {features.map(f => (
                            <Card key={f.title} className="border-hairline-dark bg-[#0a0a0a]">
                                <CardContent className="pt-6">
                                    <div className="h-10 w-10 border border-hairline-dark rounded flex items-center justify-center mb-4">
                                        <f.icon className="h-5 w-5 text-white" />
                                    </div>
                                    <h3 className="font-semibold mb-2 uppercase tracking-wide text-sm">{f.title}</h3>
                                    <p className="text-sm text-white/60">{f.desc}</p>
                                </CardContent>
                            </Card>
                        ))}
                    </div>
                </div>
            </section>

            {/* Pricing */}
            <section className="py-24 px-6 lg:px-10 bg-black">
                <div className="max-w-5xl mx-auto">
                    <div className="mb-16">
                        <div className="flex items-center gap-4 mb-4">
                            <div className="w-px h-5 bg-white/60" />
                            <span className="micro-cap text-white/60 tracking-[0.2em]">Precios</span>
                        </div>
                        <h2 className="text-white mb-4">Planes</h2>
                        <p className="text-white/60">Elegí el plan que mejor se adapte a tu gimnasio.</p>
                    </div>
                    <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3 max-w-5xl mx-auto">
                        {plans.map(plan => (
                            <Card key={plan.name} className={plan.popular ? 'border-white' : 'border-hairline-dark bg-[#0a0a0a]'}>
                                <div className="p-6">
                                    {plan.popular && <Badge className="mb-4">Más popular</Badge>}
                                    <h3 className="text-2xl font-bold uppercase tracking-wide mb-1">{plan.name}</h3>
                                    <p className="text-3xl font-bold mt-2">{plan.price}</p>
                                </div>
                                <div className="px-6 pb-6">
                                    <ul className="space-y-3">
                                        {plan.features.map(f => (
                                            <li key={f} className="flex items-center gap-2 text-sm text-white/80">
                                                <Check className="h-4 w-4 text-white shrink-0" />
                                                {f}
                                            </li>
                                        ))}
                                    </ul>
                                    <Link to="/register">
                                        <Button className="w-full mt-6 text-xs" variant={plan.popular ? 'default' : 'outline'}>
                                            Empezar con Plan {plan.name}
                                        </Button>
                                    </Link>
                                </div>
                            </Card>
                        ))}
                    </div>
                </div>
            </section>

            {/* Footer */}
            <footer className="border-t border-hairline-dark py-10 px-6 lg:px-10 bg-black">
                <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-6">
                    <div className="flex flex-col sm:flex-row items-center gap-6">
                        <div className="flex items-center gap-3">
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="text-white">
                                <path d="M12 2l8.66 5v10l-8.66 5-8.66-5V7z" />
                                <path d="M12 12m-3 0a3 3 0 1 0 6 0a3 3 0 1 0 -6 0" />
                            </svg>
                            <span className="font-semibold uppercase tracking-[0.2em] text-sm">GYMS.ar</span>
                        </div>
                        <nav className="flex flex-wrap justify-center items-center gap-x-6 gap-y-2">
                            <Link to="/precios" className="micro-cap text-white/60 hover:text-white transition-colors">Precios</Link>
                            <Link to="/legal/privacidad" className="micro-cap text-white/60 hover:text-white transition-colors">Privacidad</Link>
                            <Link to="/legal/terminos" className="micro-cap text-white/60 hover:text-white transition-colors">Términos</Link>
                            <Link to="/legal/pagos" className="micro-cap text-white/60 hover:text-white transition-colors">Pagos</Link>
                            <Link to="/legal/cookies" className="micro-cap text-white/60 hover:text-white transition-colors">Cookies</Link>
                        </nav>
                    </div>
                    <div className="flex items-center gap-4">
                        <p className="caption text-white/50">
                            <span className="hover:text-white transition-colors">GYMS.ar</span>
                            {' - Powered by '}
                            <a href="https://beira.ar" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">Beira<span className="text-[#7ad03a]">.</span>ar</a>
                        </p>
                        <a
                            href="https://wa.me/5493872265797"
                            target="_blank"
                            rel="noopener noreferrer"
                            aria-label="WhatsApp"
                            className="text-[#7ad03a] hover:text-white transition-colors"
                        >
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
                            </svg>
                        </a>
                    </div>
                </div>
            </footer>
        </div>
    );
}
